import { useMemo, useState } from "react";
import { AuditFilterBar } from "@/components/audit/AuditFilterBar";
import { AuditTimeline } from "@/components/audit/AuditTimeline";
import { useAuditEvents } from "@/hooks/useAuditEvents";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { sinceDate } from "@/components/tokens/PeriodFilter";

export interface AuditPanelProps {
  projectSlug: string;
}

/**
 * Project portal audit tab (SD3). Thin composition — owns filter state,
 * pins `projectId: projectSlug` into every request (not user-editable at
 * project level, unlike the org page's project selector, SD4), and
 * renders AuditFilterBar + AuditTimeline.
 */
export function AuditPanel({ projectSlug }: AuditPanelProps) {
  const [eventKind, setEventKind] = useState("");
  const [rawWorkItemRef, setRawWorkItemRef] = useState("");
  const workItemRef = useDebouncedValue(rawWorkItemRef, 300);
  const [periodDays, setPeriodDays] = useState(30);

  const filters = useMemo(
    () => ({
      projectId: projectSlug,
      eventKind: eventKind || undefined,
      workItemRef: workItemRef || undefined,
      since: sinceDate(periodDays),
    }),
    [projectSlug, eventKind, workItemRef, periodDays],
  );

  const { events, total, earliestEventAt, loading, error, refetch } =
    useAuditEvents(filters);

  return (
    <div className="space-y-4">
      <AuditFilterBar
        eventKind={eventKind}
        onEventKindChange={setEventKind}
        workItemRef={rawWorkItemRef}
        onWorkItemRefChange={setRawWorkItemRef}
        periodDays={periodDays}
        onPeriodDaysChange={setPeriodDays}
      />
      <AuditTimeline
        events={events}
        total={total}
        earliestEventAt={earliestEventAt}
        loading={loading}
        error={error}
        onRetry={refetch}
      />
    </div>
  );
}
